// ═══════════════════════════════════════════════════════════════
// CONTACT ACTIVITY REPORT
// Path: src/components/reports/ContactActivityReport.jsx
//
// Summarizes contact log activity for a date range.
//   1. KPI tiles — total contacts, unique donors, avg per donor
//   2. Bar chart — contacts by type
//   3. Bar chart — contacts by purpose
//   4. Detail table — every contact with donor name, CSV exportable
//
// Self-contained — fetches its own data from contact_logs table.
// ═══════════════════════════════════════════════════════════════

import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  MessageSquare,
  Users,
  Calendar,
  Download,
  TrendingUp,
} from "lucide-react";
import { useApp } from "../../context/AppContext";
import { FONT_SIZES, FONT_FAMILY, ICON_SIZES } from "../../constants/appConfig";
import { CONTACT_LOG_TYPES, CONTACT_PURPOSES } from "../../constants/contactTypes";
import { Card } from "../ui/Card";
import { contactLogService } from "../../services/contactLogService";

// ─── Helpers ───
const fmtDate = (dateStr) => {
  if (!dateStr) return "—";
  const d = new Date(dateStr);
  return d.toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const getTypeName = (typeId) =>
  CONTACT_LOG_TYPES.find((t) => t.id === typeId)?.name || typeId || "Unknown";

const getTypeColor = (typeId) =>
  CONTACT_LOG_TYPES.find((t) => t.id === typeId)?.color || "#6b7280";

const getPurposeName = (purposeId) =>
  CONTACT_PURPOSES.find((p) => p.id === purposeId)?.name || purposeId;

const getPurposeColor = (purposeId) =>
  CONTACT_PURPOSES.find((p) => p.id === purposeId)?.color || "#6b7280";

// ─── Reusable stat card ───
const StatCard = ({ label, value, sub, color, palette }) => (
  <Card style={{ textAlign: "center", padding: 20 }}>
    <div
      style={{
        fontSize: "1.8rem",
        fontWeight: 700,
        color: color || palette.accent1,
        lineHeight: 1,
      }}
    >
      {value}
    </div>
    <div
      style={{
        fontSize: FONT_SIZES.sm,
        fontWeight: 600,
        color: palette.text1,
        marginTop: 6,
      }}
    >
      {label}
    </div>
    {sub && (
      <div style={{ fontSize: FONT_SIZES.xs, color: palette.text3, marginTop: 2 }}>
        {sub}
      </div>
    )}
  </Card>
);

// ─── Horizontal bar chart (pure CSS) ───
const BarChart = ({ data, palette }) => {
  const maxVal = Math.max(...data.map((d) => d.value), 1);
  const total = data.reduce((s, x) => s + x.value, 0);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
      {data.map((d) => (
        <div
          key={d.label}
          style={{ display: "flex", alignItems: "center", gap: 12 }}
        >
          <div
            style={{
              width: 120,
              fontSize: FONT_SIZES.sm,
              color: palette.text1,
              fontWeight: 500,
              textAlign: "right",
              flexShrink: 0,
            }}
          >
            {d.label}
          </div>
          <div
            style={{
              flex: 1,
              height: 28,
              borderRadius: 6,
              background: `${palette.accent1}08`,
              overflow: "hidden",
              position: "relative",
            }}
          >
            <div
              style={{
                height: "100%",
                borderRadius: 6,
                background: d.color || palette.accent1,
                width: `${(d.value / maxVal) * 100}%`,
                transition: "width 0.4s ease",
                display: "flex",
                alignItems: "center",
                justifyContent: "flex-end",
                paddingRight: 8,
              }}
            >
              {d.value > 0 && (
                <span
                  style={{
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 700,
                    color: "#fff",
                    textShadow: "0 1px 2px rgba(0,0,0,0.3)",
                  }}
                >
                  {d.value}
                </span>
              )}
            </div>
          </div>
          <div
            style={{
              width: 36,
              fontSize: FONT_SIZES.xs,
              color: palette.text3,
              textAlign: "right",
            }}
          >
            {total > 0 ? Math.round((d.value / total) * 100) : 0}%
          </div>
        </div>
      ))}
    </div>
  );
};

// ─── Section header ───
const SectionHeader = ({ icon: SI, title, palette }) => (
  <h3
    style={{
      display: "flex",
      alignItems: "center",
      gap: 8,
      margin: "0 0 16px",
      fontSize: FONT_SIZES.md,
      fontWeight: 600,
      color: palette.text1,
      paddingBottom: 12,
      borderBottom: `1px solid ${palette.cardBorder}`,
    }}
  >
    <SI size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
    {title}
  </h3>
);

// ═══════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════
export const ContactActivityReport = () => {
  const { palette, members } = useApp();

  // Date range — default to last 30 days
  const today = new Date().toISOString().slice(0, 10);
  const thirtyDaysAgo = new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10);
  const [dateFrom, setDateFrom] = useState(thirtyDaysAgo);
  const [dateTo, setDateTo] = useState(today);
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);

  // Quick range buttons
  const quickRanges = useMemo(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = now.getMonth() + 1;
    const monthStr = `${year}-${String(month).padStart(2, "0")}-01`;
    const lastMonthStart = month === 1
      ? `${year - 1}-12-01`
      : `${year}-${String(month - 1).padStart(2, "0")}-01`;
    const lastMonthEnd = new Date(year, month - 1, 0).toISOString().slice(0, 10);
    const ninetyDaysAgo = new Date(Date.now() - 90 * 86400000).toISOString().slice(0, 10);

    return [
      { label: "Last 30 Days", from: thirtyDaysAgo, to: today },
      { label: "This Month", from: monthStr, to: today },
      { label: "Last Month", from: lastMonthStart, to: lastMonthEnd },
      { label: "Last 90 Days", from: ninetyDaysAgo, to: today },
      { label: `YTD ${year}`, from: `${year}-01-01`, to: today },
    ];
  }, []);

  // Fetch logs
  const loadLogs = useCallback(async () => {
    setLoading(true);
    try {
      const data = await contactLogService.getSince(dateFrom);
      // Filter to dateTo on client side
      const filtered = data.filter((log) => {
        const logDate = (log.logDate || "").slice(0, 10);
        return logDate <= dateTo;
      });
      setLogs(filtered);
    } catch (err) {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo]);

  useEffect(() => { loadLogs(); }, [loadLogs]);

  // Build member lookup
  const memberMap = useMemo(() => {
    const map = {};
    members.forEach((m) => { map[m.id] = m; });
    return map;
  }, [members]);

  // ── Computed stats ──
  const stats = useMemo(() => {
    const totalContacts = logs.length;
    const uniqueDonorIds = new Set(logs.map((l) => l.memberId));
    const uniqueDonors = uniqueDonorIds.size;
    const avgPerDonor = uniqueDonors > 0 ? (totalContacts / uniqueDonors).toFixed(1) : "0";

    // By type
    const typeCounts = {};
    logs.forEach((l) => {
      typeCounts[l.type] = (typeCounts[l.type] || 0) + 1;
    });
    const byType = CONTACT_LOG_TYPES
      .map((t) => ({
        label: t.name,
        value: typeCounts[t.id] || 0,
        color: t.color,
      }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);

    // By purpose (multi-select — a single log can have multiple purposes)
    const purposeCounts = {};
    logs.forEach((l) => {
      if (l.purpose && Array.isArray(l.purpose)) {
        l.purpose.forEach((p) => {
          purposeCounts[p] = (purposeCounts[p] || 0) + 1;
        });
      }
    });
    const byPurpose = CONTACT_PURPOSES
      .map((p) => ({
        label: p.name,
        value: purposeCounts[p.id] || 0,
        color: p.color,
      }))
      .filter((d) => d.value > 0)
      .sort((a, b) => b.value - a.value);

    // By direction (for types that have it)
    const inbound = logs.filter((l) => l.direction === "inbound").length;
    const outbound = logs.filter((l) => l.direction === "outbound").length;
    const noDirection = totalContacts - inbound - outbound;

    // Top contacted donors
    const donorCounts = {};
    logs.forEach((l) => {
      donorCounts[l.memberId] = (donorCounts[l.memberId] || 0) + 1;
    });
    const topDonors = Object.entries(donorCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([memberId, count]) => {
        const member = memberMap[memberId];
        const name = member
          ? member.donorType === "organization"
            ? member.organization || "Unknown Organization"
            : `${member.lastName || ""}, ${member.firstName || ""}`.replace(/^, |, $/, "") || "Unknown"
          : "Unknown";
        return { name, count, memberId };
      });

    return {
      totalContacts,
      uniqueDonors,
      avgPerDonor,
      byType,
      byPurpose,
      inbound,
      outbound,
      noDirection,
      topDonors,
    };
  }, [logs, memberMap]);

  // ── Detail rows with donor names ──
  const detailRows = useMemo(() => {
    return logs.map((log) => {
      const member = memberMap[log.memberId];
      const donorName = member
        ? member.donorType === "organization"
          ? member.organization || "Unknown Organization"
          : `${member.lastName || ""}, ${member.firstName || ""}`.replace(/^, |, $/, "") || "Unknown"
        : "Unknown";
      return {
        ...log,
        donorName,
        typeName: getTypeName(log.type),
        purposeNames: (log.purpose || []).map(getPurposeName).join(", "),
        dateFormatted: fmtDate(log.logDate),
        dateSortable: (log.logDate || "").slice(0, 10),
      };
    });
  }, [logs, memberMap]);

  // ── CSV Export ──
  const handleExport = () => {
    const header = "Date,Donor,Type,Direction,Subject,Purpose,Notes";
    const csvRows = detailRows.map((r) => {
      const fields = [
        r.dateSortable,
        r.donorName,
        r.typeName,
        r.direction || "",
        r.subject || "",
        r.purposeNames,
        (r.notes || "").replace(/\n/g, " "),
      ];
      return fields.map((f) => `"${String(f).replace(/"/g, '""')}"`).join(",");
    });
    const csv = [header, ...csvRows].join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `Contact_Activity_${dateFrom}_to_${dateTo}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <>
      {/* ── Date range picker ── */}
      <Card style={{ marginBottom: 16 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: 12,
            flexWrap: "wrap",
          }}
        >
          <Calendar size={ICON_SIZES.sm} style={{ color: palette.accent1 }} />
          <input
            type="date"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg,
              color: palette.inputColor,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
            }}
          />
          <span style={{ color: palette.text3, fontSize: FONT_SIZES.sm }}>to</span>
          <input
            type="date"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            style={{
              padding: "6px 10px",
              borderRadius: 6,
              border: `1px solid ${palette.inputBorder}`,
              background: palette.inputBg,
              color: palette.inputColor,
              fontSize: FONT_SIZES.sm,
              fontFamily: FONT_FAMILY,
            }}
          />
        </div>
        <div
          style={{
            display: "flex",
            gap: 6,
            marginTop: 10,
            flexWrap: "wrap",
          }}
        >
          {quickRanges.map((qr) => (
            <button
              key={qr.label}
              onClick={() => { setDateFrom(qr.from); setDateTo(qr.to); }}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                border: `1px solid ${palette.inputBorder}`,
                background:
                  dateFrom === qr.from && dateTo === qr.to
                    ? palette.accent1
                    : palette.inputBg,
                color:
                  dateFrom === qr.from && dateTo === qr.to
                    ? "#fff"
                    : palette.text2,
                fontSize: FONT_SIZES.xs,
                fontWeight: 600,
                fontFamily: FONT_FAMILY,
                cursor: "pointer",
                transition: "all 0.15s",
              }}
            >
              {qr.label}
            </button>
          ))}
        </div>
      </Card>

      {loading ? (
        <Card style={{ textAlign: "center", padding: 40 }}>
          <span style={{ color: palette.text3, fontSize: FONT_SIZES.sm }}>
            Loading contact activity...
          </span>
        </Card>
      ) : (
        <>
          {/* ── KPI Tiles ── */}
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
              gap: 12,
              marginBottom: 16,
            }}
          >
            <StatCard
              label="Total Contacts"
              value={stats.totalContacts}
              palette={palette}
            />
            <StatCard
              label="Donors Reached"
              value={stats.uniqueDonors}
              color="#10b981"
              palette={palette}
            />
            <StatCard
              label="Avg / Donor"
              value={stats.avgPerDonor}
              color="#8b5cf6"
              palette={palette}
            />
            {stats.outbound > 0 && (
              <StatCard
                label="Outbound"
                value={stats.outbound}
                color="#3b82f6"
                palette={palette}
              />
            )}
            {stats.inbound > 0 && (
              <StatCard
                label="Inbound"
                value={stats.inbound}
                color="#f59e0b"
                palette={palette}
              />
            )}
          </div>

          {/* ── Contacts by Type ── */}
          {stats.byType.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader
                icon={MessageSquare}
                title="Contacts by Type"
                palette={palette}
              />
              <BarChart data={stats.byType} palette={palette} />
            </Card>
          )}

          {/* ── Contacts by Purpose ── */}
          {stats.byPurpose.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader
                icon={TrendingUp}
                title="Contacts by Purpose"
                palette={palette}
              />
              <BarChart data={stats.byPurpose} palette={palette} />
            </Card>
          )}

          {/* ── Top Contacted Donors ── */}
          {stats.topDonors.length > 0 && (
            <Card style={{ marginBottom: 16 }}>
              <SectionHeader
                icon={Users}
                title="Most Contacted Donors"
                palette={palette}
              />
              <BarChart
                data={stats.topDonors.map((d) => ({
                  label: d.name,
                  value: d.count,
                  color: palette.accent1,
                }))}
                palette={palette}
              />
            </Card>
          )}

          {/* ── Detail Table ── */}
          <Card>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 16,
                paddingBottom: 12,
                borderBottom: `1px solid ${palette.cardBorder}`,
              }}
            >
              <h3
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  margin: 0,
                  fontSize: FONT_SIZES.md,
                  fontWeight: 600,
                  color: palette.text1,
                }}
              >
                <Calendar size={ICON_SIZES.md} style={{ color: palette.accent1 }} />
                Contact Detail ({detailRows.length})
              </h3>
              {detailRows.length > 0 && (
                <button
                  onClick={handleExport}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    padding: "6px 14px",
                    borderRadius: 8,
                    border: `1px solid ${palette.accent1}`,
                    background: `${palette.accent1}08`,
                    color: palette.accent1,
                    fontSize: FONT_SIZES.xs,
                    fontWeight: 600,
                    fontFamily: FONT_FAMILY,
                    cursor: "pointer",
                  }}
                >
                  <Download size={ICON_SIZES.sm} /> Export CSV
                </button>
              )}
            </div>

            <div
              style={{
                overflowX: "auto",
                borderRadius: 8,
                border: `1px solid ${palette.cardBorder}`,
              }}
            >
              <table
                style={{
                  width: "100%",
                  borderCollapse: "collapse",
                  fontSize: FONT_SIZES.sm,
                  fontFamily: FONT_FAMILY,
                }}
              >
                <thead>
                  <tr style={{ background: `${palette.accent1}08` }}>
                    {["Date", "Donor", "Type", "Subject", "Purpose"].map((col) => (
                      <th
                        key={col}
                        style={{
                          padding: "10px 14px",
                          textAlign: "left",
                          fontWeight: 600,
                          color: palette.text1,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {col}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {detailRows.map((row, idx) => (
                    <tr
                      key={row.id || idx}
                      style={{
                        background:
                          idx % 2 === 0 ? "transparent" : `${palette.accent1}03`,
                      }}
                    >
                      <td
                        style={{
                          padding: "8px 14px",
                          color: palette.text1,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.dateFormatted}
                      </td>
                      <td
                        style={{
                          padding: "8px 14px",
                          color: palette.text1,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          fontWeight: 500,
                        }}
                      >
                        {row.donorName}
                      </td>
                      <td
                        style={{
                          padding: "8px 14px",
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          whiteSpace: "nowrap",
                        }}
                      >
                        <span
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 4,
                            padding: "2px 8px",
                            borderRadius: 4,
                            background: `${getTypeColor(row.type)}15`,
                            color: getTypeColor(row.type),
                            fontSize: FONT_SIZES.xs,
                            fontWeight: 600,
                          }}
                        >
                          {row.typeName}
                          {row.direction && (
                            <span style={{ fontSize: "0.55rem", opacity: 0.8 }}>
                              ({row.direction === "inbound" ? "in" : "out"})
                            </span>
                          )}
                        </span>
                      </td>
                      <td
                        style={{
                          padding: "8px 14px",
                          color: palette.text2,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          maxWidth: 240,
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {row.subject || "—"}
                      </td>
                      <td
                        style={{
                          padding: "8px 14px",
                          color: palette.text3,
                          borderBottom: `1px solid ${palette.cardBorder}`,
                          fontSize: FONT_SIZES.xs,
                        }}
                      >
                        {row.purposeNames || "—"}
                      </td>
                    </tr>
                  ))}
                  {detailRows.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        style={{
                          padding: 24,
                          textAlign: "center",
                          color: palette.text3,
                        }}
                      >
                        No contacts logged in this period
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </>
      )}
    </>
  );
};
